import { isString } from "@sinch/feature-flags";
import type { DeepPartial } from "simplytyped";
import { v4 as uuidv4 } from "uuid";
import type { Accounting, AccountInfo } from "./api/engage/engage.types";
import type { ShellUser } from "./api/types";
import type { FeatureFlaggingContract } from "./featureFlagsUserContract";
import { safeRetrieveValue } from "./helpers/safeRetrieveValue";
import { getSessionStorage, setSessionStorage } from "./helpers/sessionStorage";

const na = "N/A";

type FeatureFlaggingMMContract = Omit<FeatureFlaggingContract, "custom"> & {
  custom: Omit<
    FeatureFlaggingContract["custom"],
    "projectId" | "channelId" | "language" | "trialStart" | "contractStart"
  > & {
    emailDomain: string;
    vendor: string;
    hiveBillingAccountId: string;
  };
};

export const getFeatureFlagsMMContract = (
  userAccount: ShellUser,
  location: Window["location"],
  hiveBillingAccountId: string
): FeatureFlaggingMMContract => ({
  identifier: userAccount.account?.id ?? getSessionId(),
  email: userAccount?.email ?? na,
  country: userAccount?.account?.country?.toLowerCase() ?? na,
  custom: {
    // Account based
    accountId: userAccount.account?.id || "",
    emailDomain: userAccount?.email
      ? userAccount.email.slice(userAccount.email.indexOf("@") + 1)
      : na,
    vendor: safeRetrieveValue(userAccount.settings, "vendorId", isString) ?? na,
    // Environment based
    host: location.host,
    hostname: location.hostname,
    search: location.search,
    hiveBillingAccountId,
  },
});

/**
 * Converts input data into data matching our `FeatureFlaggingContract`. The
 * function returns a ConfigCat user object. The `FeatureFlaggingContract` type
 * should match `CCUser` and help us uphold the contract with the targeting
 * rules in ConfigCat.
 */
export const getFeatureFlagsUserContract = (
  user: ShellUser,
  accounting: DeepPartial<Accounting>,
  accountInfo: DeepPartial<AccountInfo>,
  location: Window["location"]
): FeatureFlaggingContract => ({
  identifier: (user.id ?? na).toString(),
  email: user.email ?? na,
  country: accounting.country?.toLowerCase() ?? na,
  custom: {
    // Account based
    accountId: String(accounting.account_id ?? ""),
    trialStart: accountInfo.testphase_start?.toString() || na,
    contractStart: accountInfo.contract_start?.toString() || na,

    // User based
    /** @deprecated Use projectId instead. */
    channelId: (typeof user.settings?.activeChannel === "number"
      ? user.settings.activeChannel
      : na
    ).toString(),
    projectId: (user.activeProject ?? na).toString(),
    language: user.language ?? na,
    emailDomain: user.email
      ? user.email.slice(user.email.indexOf("@") + 1)
      : na,
    // Environment based
    host: location.host,
    hostname: location.hostname,
    search: location.search,
  },
});

const SESSION_ID = "SESSION_ID";

export const getSessionId = () => {
  const existingSession = getSessionStorage(
    SESSION_ID,
    (x): x is string | null => isString(x, SESSION_ID),
    null
  );

  if (!existingSession) {
    const newSession = uuidv4();
    setSessionStorage(SESSION_ID, newSession);
    return newSession;
  }

  return existingSession;
};

export const getFeatureFlagSessionContract = () => ({
  identifier: getSessionId(),
  custom: {
    language: window.navigator.language,
    host: location.host,
    hostname: location.hostname,
    search: location.search,
  },
});
